import React, { useEffect, useMemo, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import useOneWayIn from '../../hooks/use-one-way-in';
import { Feltgruppe, GruppertSkjema } from 'src/api/v2';
import { useTranslation } from 'react-i18next';
import { PageParams } from '../types/PageParams';
import { Alert, Spinner } from '@innovationnorway/cl-react';
import OwiFormFieldGroup from '../components/OwiForm/OwiFormFieldGroup';
import { useCluster } from 'src/features/cluster';
import OpportunityFeedback from '../components/OwiReferralFeedback/OpportunityFeedback';
import useOneWayInFeedback from 'src/features/one-way-in/hooks/use-one-way-in-feedback';
import useFeedbackMissing from 'src/features/one-way-in/hooks/use-feedback-missing';
import OpportunityTimeline from '../components/OpportunityTimeline';

const Opportunity: React.FC = () => {
  const { formId } = useParams<PageParams>();
  const location = useLocation();
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const { cluster } = useCluster();
  const {
    oneWayInGroupedFormWOEnrollmentsQuery,
    oneWayInGroupedFormEnrollment,
    oneWayInGroupedFormCustomerRoles,
    oneWayInOpportunityById,
    onewWayInOpportunityFormINResponseMailQuery,
  } = useOneWayIn(formId);
  const { opportunityFeedbackCollection, isLoading: isLoadingFeedback } = useOneWayInFeedback(formId);
  const feedbackMissing = useFeedbackMissing(opportunityFeedbackCollection, oneWayInOpportunityById.data);
  const isAutomaticallyReferred = onewWayInOpportunityFormINResponseMailQuery.data?.isAutomaticReply;

  const form = oneWayInGroupedFormWOEnrollmentsQuery.data;
  const enrollment = oneWayInGroupedFormEnrollment.data;
  const customerRoles = oneWayInGroupedFormCustomerRoles.data;

  // Filter out previous applications and roles to hide it from RaskAvklaringViser because this is moved to CompanyInfo
  const groupsWithoutApplicationsAndRoles = useMemo(() => {
    if (!form?.grupper) {
      return [];
    }

    // Create a deep copy of the form to avoid mutating the original object that is cached in the query, because it is used in CompanyInfo
    const formCopy = JSON.parse(JSON.stringify(form)) as GruppertSkjema;
    if (!formCopy.grupper) {
      return [];
    }

    const groups = formCopy.grupper.filter((group) => group.navn !== 'Tidligere søknader') as Feltgruppe[];
    if (enrollment) groups.push(enrollment);

    const aboutField = groups.find((group) => group.navn === 'Om bedriften');
    if (aboutField?.felter) {
      aboutField.felter = aboutField.felter.filter((field) => field.navn !== 'Roller i bedriften');
    }

    const customerField = groups.find((group) => group.navn === 'Om kunden');
    if (customerField && customerRoles?.felter) {
      customerField.felter = [...(customerField.felter ?? []), ...customerRoles.felter];
    }

    return groups;
  }, [customerRoles, enrollment, form]);

  const groupNames = useMemo(
    () =>
      groupsWithoutApplicationsAndRoles
        .map((group) => group.navn?.trim() ?? '')
        .filter((name) => name.length > 0),
    [groupsWithoutApplicationsAndRoles],
  );

  // Scroll down to the anchor on load, has to wait for the form to be loaded and rendered before scrolling
  const dataReadyToScroll =
    oneWayInGroupedFormWOEnrollmentsQuery.isSuccess &&
    oneWayInGroupedFormEnrollment.isFetched &&
    oneWayInGroupedFormCustomerRoles.isFetched;
  const hasScrolledToHashRef = useRef(false);
  useEffect(() => {
    if (hasScrolledToHashRef.current || !dataReadyToScroll) return;
    const id = decodeURIComponent(location.hash.replace('#', ''));
    if (!id) return;
    const el = document.getElementById(id);
    const container = document.querySelector('.owi-form--container');
    if (!el || !container) return;
    hasScrolledToHashRef.current = true;

    const scrollToTarget = () => {
      const stickyHeader = document.querySelector('.opportunity-details--sticky-header');
      const offset = (stickyHeader?.getBoundingClientRect().height ?? 0) + 16;
      window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - offset });
    };

    let idleTimeout: ReturnType<typeof setTimeout>;
    const stop = () => {
      clearTimeout(idleTimeout);
      observer.disconnect();
      window.removeEventListener('wheel', stop);
      window.removeEventListener('touchmove', stop);
      window.removeEventListener('keydown', stop);
    };

    const observer = new ResizeObserver(() => {
      scrollToTarget();
      // Disconnect once the content height has been stable for a moment.
      clearTimeout(idleTimeout);
      idleTimeout = setTimeout(stop, 200);
    });
    observer.observe(container);
    scrollToTarget();

    // Let the user take over immediately if they scroll.
    window.addEventListener('wheel', stop, { passive: true });
    window.addEventListener('touchmove', stop, { passive: true });
    window.addEventListener('keydown', stop);

    return stop;
  }, [dataReadyToScroll, location.hash]);

  if (oneWayInGroupedFormWOEnrollmentsQuery.isError) {
    return (
      <Alert data-color="danger" role="alert">
        {tOneWayIn('errorCouldNotGetForm')}
      </Alert>
    );
  }

  if (form === undefined) {
    return <></>;
  }

  return (
    <div>
      {groupsWithoutApplicationsAndRoles.length === 0 ? (
        <Spinner />
      ) : (
        <div className="opportunity-details--content">
          <OpportunityTimeline stepNames={groupNames} />
          <div className="display--flex flex-direction--column gap--ds-12 owi-form--container">
            {feedbackMissing &&
              !onewWayInOpportunityFormINResponseMailQuery.isLoading &&
              !isLoadingFeedback &&
              !isAutomaticallyReferred && (
                <OpportunityFeedback
                  formId={formId}
                  opportunity={oneWayInOpportunityById.data}
                  cluster={cluster}
                />
              )}
            {groupsWithoutApplicationsAndRoles.map((group, index) => (
              <OwiFormFieldGroup
                key={group.navn ?? `group-${index}`}
                fieldGroup={group}
                lastItem={index === groupsWithoutApplicationsAndRoles.length - 1}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Opportunity;
